import React, { useMemo, useState } from 'react'
import { useNotification } from '../../../../hooks/useNotification';
import { useErrorInputs } from '../../../../hooks/useErrorInputs';
import { useForm } from '../../../../hooks/useForm';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import Close from '@mui/icons-material/Close';
import { ActionButtons } from '../../../../components/others/ActionButtons';
import { ManageActions } from '../manageconfiguration/ManageActions';
import TableActionBasic from '../TableActionBasic';
import { CHILD_TYPE_ACTIONS, EMPTY_ACTION, MENU_TYPE_ACTIONS } from '../../constants/interface.constants';
import { parseFlowProcess, sanitizeAction } from '../../helpers/helpers';

export const ManageFlowProcess = ({ data, typeAction, isDisabled, isEdit, addedFlowProcess, t, inline = false }) => {
    const [values, handleInputChange, handleDirectInputChange, setAllInfo] = useForm(EMPTY_ACTION);
    const [errorInputs, , resetErrors, validForm] = useErrorInputs({});
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [sendNotification] = useNotification();

    const currentFlowProcess = useMemo(() => parseFlowProcess(data), [data]);

    const supportsLineActions = ['MENU', 'SUBMENU', 'CHART_REPORT'].includes(typeAction);

    const handleOpenModal = (item = null, index = null) => {
        resetErrors();
        setSelectedIndex(index);
        setAllInfo(item || EMPTY_ACTION);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        resetErrors();
        setAllInfo(EMPTY_ACTION);
        setSelectedIndex(null);
    };

    const handleSave = () => {
        if (!validForm(['accion', 'icon', 'color', 'type'], values)) {
            sendNotification({ msg: 'Debe completar el formulario.', variant: 'error' });
            return;
        }

        const action = sanitizeAction(values);

        if (CHILD_TYPE_ACTIONS.includes(typeAction)) {
            addedFlowProcess('configuration', action);
            handleCloseModal();
            return;
        }

        let updatedFlow;
        if (selectedIndex === null)
            updatedFlow = [...currentFlowProcess, action];
        else
            updatedFlow = currentFlowProcess.map((item, i) => i === selectedIndex ? sanitizeAction({ ...item, ...values }) : item);

        addedFlowProcess('flow_process', updatedFlow);
        handleCloseModal();
    };

    if (!supportsLineActions) return null;

    const triggerButton = (
        <Tooltip title={t('interface.addFlowProcess')}>
            <span>
                <IconButton
                    disabled={isDisabled}
                    onClick={() => handleOpenModal()}
                    size="small"
                    sx={{ p: 0.5, height: 20 }}
                >
                    <SettingsInputComponentIcon />
                </IconButton>
            </span>
        </Tooltip>
    );

    return (
        <Box sx={{ width: inline ? 'auto' : '100%' }}>
            {!inline && (
                <Box sx={{ pt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        {triggerButton}
                    </Box>
                </Box>
            )}

            {inline && triggerButton}

            {!inline && currentFlowProcess.length > 0 && (
                <TableActionBasic
                    columns={[
                        { label: 'Nombre', id: 'accion' },
                        { label: 'Tipo', id: 'type' },
                    ]}
                    values={currentFlowProcess}
                    handleDelete={(_, idx) => {
                        addedFlowProcess('flow_process', currentFlowProcess.filter((__, i) => i !== idx));
                    }}
                    handleEdit={(_, idx) => handleOpenModal(currentFlowProcess[idx], idx)}
                    t={t}
                />
            )}

            <Dialog open={openModal} fullWidth maxWidth='md'>
                <DialogTitle>
                    <Typography variant="h5" component="span">
                        {selectedIndex !== null ? t('interface.editFlowProcess') : t('interface.createEditFlowProcess')}
                    </Typography>
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <ManageActions
                        values={values}
                        errorInputs={errorInputs}
                        listActions={false}
                        handleInputChange={handleInputChange}
                        handleDirectInputChange={handleDirectInputChange}
                        t={t}
                    />
                </DialogContent>
                <DialogActions>
                    <ActionButtons onSubmit={handleSave} onCancel={handleCloseModal} />
                </DialogActions>
            </Dialog>
        </Box>
    );
};
